import { arrayMove } from "@dnd-kit/sortable"
import { CatalogItem } from "../../domain/catalogItem"
import { ProductionRate } from '../../domain/productionRate'
import { hasValue } from '../../helpers/nullable'

export type TemplateSummary = {
  id: number
  name: string
  categories?: string
}

export type Template = {
  id: number
  name: string
  items: TemplateItem[]
  categories: string[]
  workareaInternalNotes?: string
  workareaCustomerAndInvoiceNotes?: string
  workareaInvoiceDescription?: string
}

export type TemplateItem = {
  id: number
  name: string
  catalogName: string
  units: string
  catalogItemGuid: string
  quantity: number
  rateInstallationQuantity?: number
  ratePerUnits?: string
  rateUsageQuantity?: number
  sortIndex: number
}

export const defaultTemplate = (): Template => ({
  id: 0,
  name: '',
  items: [] as TemplateItem[],
  categories: [] as string[]
})

export const defaultTemplateItem = (): TemplateItem => ({
  id: 0,
  name: '',
  catalogName: '',
  units: '',
  catalogItemGuid: '',
  quantity: 0,
  sortIndex: 0,
})

export type TemplateState = {
  name?: string
  categories?: string[]
  workareaInternalNotes?: string
  workareaCustomerAndInvoiceNotes?: string
  workareaInvoiceDescription?: string
  addedItems: TemplateItem[]
  updatedItems: TemplateItem[]
  deletedItemIds: number[]
}

export const defaultTemplateState = (): TemplateState => ({
  addedItems: [],
  updatedItems: [],
  deletedItemIds: []
})

export type ViewItem = TemplateItem & {
  isEmptyRow?: boolean
  isEntireRowDisabled: boolean
  isDeleted: boolean
  dragId: string
}

export type Action =
  | { type: 'NAME_UPDATED', value: string }
  | { type: 'CATEGORIES_UPDATED', value: string[] }
  | { type: 'WORKAREA_INTERNAL_NOTES_UPDATED', value: string }
  | { type: 'WORKAREA_CUSTOMER_AND_INVOICE_NOTES_UPDATED', value: string }
  | { type: 'WORKAREA_INVOICE_DESCRIPTION_UPDATED', value: string }
  | { type: 'ITEM_NAME_UPDATED', templateItemId: number, value: string, serverTemplate: Template }
  | { type: 'ITEM_NAME_RESET', templateItemId: number, serverTemplate: Template }
  | { type: 'ITEM_QUANTITY_UPDATED', templateItemId: number, value: number, serverTemplate: Template }
  | { type: 'ITEM_RATE_INSTALLATION_QUANTITY_UPDATED', templateItemId: number, value: number, serverTemplate: Template }
  | { type: 'ITEM_RATE_USAGE_QUANTITY_UPDATED', templateItemId: number, value: number, serverTemplate: Template }
  | { type: 'ITEM_RATE_PER_UNITS_UPDATED', templateItemId: number, value: string, serverTemplate: Template }
  | { type: 'PRODUCTION_RATE_UPDATED', templateItemId: number, serverTemplate: Template, productionRate: ProductionRate }
  | { type: 'PRODUCTION_RATE_CLEARED', templateItemId: number, serverTemplate: Template }
  | { type: 'ITEM_ADDED', serverTemplate: Template, catalogItem: CatalogItem, sortIndex: number, toId?: number }
  | { type: 'ITEM_DELETED', templateItemId: number }
  | { type: 'ITEM_UNDELETED', templateItemId: number }
  | { type: 'ITEM_SORTED', serverTemplate: Template, fromId: number, toId: number }
  | { type: 'TEMPLATE_SAVED' }
  | { type: 'TEMPLATE_ID_CHANGED' }

export const reducer = (state: TemplateState, action: Action): TemplateState => {
  switch (action.type) {
    case 'NAME_UPDATED':
      return { ...state, name: action.value }
    case 'CATEGORIES_UPDATED':
      return { ...state, categories: action.value }
    case 'WORKAREA_INTERNAL_NOTES_UPDATED':
      return { ...state, workareaInternalNotes: action.value }
    case 'WORKAREA_CUSTOMER_AND_INVOICE_NOTES_UPDATED':
      return { ...state, workareaCustomerAndInvoiceNotes: action.value }
    case 'WORKAREA_INVOICE_DESCRIPTION_UPDATED':
      return { ...state, workareaInvoiceDescription: action.value }
    case 'ITEM_NAME_UPDATED':
      return updateItemById(state, action.serverTemplate, action.templateItemId, x => ({ ...x, name: action.value }))
    case 'ITEM_NAME_RESET':
      return updateItemById(state, action.serverTemplate, action.templateItemId, x => ({ ...x, name: x.catalogName }))
    case 'ITEM_QUANTITY_UPDATED':
      return updateItemById(state, action.serverTemplate, action.templateItemId, x => ({ ...x, quantity: action.value }))
    case 'PRODUCTION_RATE_UPDATED':
      return updateItemById(state, action.serverTemplate, action.templateItemId, x => ({
        ...x,
        ratePerUnits: action.productionRate.ratePerUnits,
        rateInstallationQuantity: action.productionRate.rateInstallationQuantity,
        rateUsageQuantity: action.productionRate.rateUsageQuantity
      }))
    case 'PRODUCTION_RATE_CLEARED':
      return updateItemById(state, action.serverTemplate, action.templateItemId, x => ({
        ...x,
        rateInstallationQuantity: undefined,
        rateUsageQuantity: undefined,
        ratePerUnits: undefined
      }))
    case 'ITEM_ADDED':
      return addItemFromCatalog(state, action.serverTemplate, action.catalogItem, action.sortIndex, action.toId)
    case 'ITEM_DELETED':
      return deleteItem(state, action.templateItemId)
    case 'ITEM_UNDELETED':
      return undeleteItem(state, action.templateItemId)
    case 'ITEM_SORTED':
      return updateSorting(state, action.serverTemplate, action.fromId, action.toId)
    case 'TEMPLATE_SAVED':
    case 'TEMPLATE_ID_CHANGED':
      return defaultTemplateState()
    default:
      return state
  }
}

export const updateItemById = (state: TemplateState, serverTemplate: Template, id: number, update: (item: TemplateItem) => TemplateItem): TemplateState => {
  if (state.addedItems.some(x => x.id === id)) {
    return {
      ...state,
      addedItems: state.addedItems.map(x => x.id === id ? update(x) : x)
    }
  }

  if (state.updatedItems.some(x => x.id === id)) {
    return {
      ...state,
      updatedItems: state.updatedItems.map(x => x.id === id ? update(x) : x)
    }
  }

  const serverItem = serverTemplate.items.find(x => x.id === id)
  if (serverItem) {
    return {
      ...state,
      updatedItems: [
        ...state.updatedItems,
        update(serverItem)
      ]
    }
  }

  return state
}

export const deleteItem = (state: TemplateState, templateItemId: number): TemplateState => {
  if (state.deletedItemIds.some(x => x === templateItemId)) {
    return state
  }
  return {
    ...state,
    deletedItemIds: [...state.deletedItemIds, templateItemId]
  }
}

export const undeleteItem = (state: TemplateState, templateItemId: number): TemplateState => {
  return { ...state, deletedItemIds: state.deletedItemIds.filter(x => x !== templateItemId) }
}

const createTemplateItemFromCatalogItem = (catalogItem: CatalogItem): TemplateItem => {
  return {
    id: 0,
    catalogItemGuid: catalogItem.guid,
    name: catalogItem.name,
    catalogName: catalogItem.name,
    units: catalogItem.units ?? '',
    sortIndex: 0,
    quantity: 0,
    rateInstallationQuantity: catalogItem.rateInstallationQuantity,
    rateUsageQuantity: catalogItem.rateUsageQuantity,
    ratePerUnits: catalogItem.ratePerUnits
  }
}

export const addItemFromCatalog = (state: TemplateState, serverTemplate: Template, catalogItem: CatalogItem, sortIndex: number, toId?: number): TemplateState => {
  const newItem: TemplateItem = {
    ...createTemplateItemFromCatalogItem(catalogItem),
    id: -(state.addedItems.length + 1),
    sortIndex,
  }
  const updatedState = { ...state, addedItems: [...state.addedItems, newItem] }
  return hasValue(toId) ? updateSorting(updatedState, serverTemplate, newItem.id, toId) : updatedState
}

const getAllUpdatedItems = (state: TemplateState, serverItems: TemplateItem[]) => {
  return serverItems.map(x => getUpdatedItem(state, x)).concat(state.addedItems)
}

export const createViewItems = (state: TemplateState, serverItems: TemplateItem[], isTemplateDeleted: boolean): ViewItem[] => {
  const rows = getAllUpdatedItems(state, serverItems).map(x => {
    const isDeleted = state.deletedItemIds.includes(x.id)
    return {
      ...x,
      isEntireRowDisabled: isDeleted || isTemplateDeleted,
      isDeleted,
      dragId: x.id.toString()
    }
  }).sort((a, b) => a.sortIndex - b.sortIndex)

  if (rows.length === 0) {
    return [{ ...defaultTemplateItem(), isEmptyRow: true, isEntireRowDisabled: true, isDeleted: false, dragId: 'empty-row' }]
  }

  return rows
}

const getUpdatedItem = (state: TemplateState, serverItem: TemplateItem): TemplateItem => {
  const updated = state.updatedItems.find(x => x.id === serverItem.id)
  return updated ?? serverItem
}

export const updateSorting = (state: TemplateState, serverTemplate: Template, fromId: number, toId: number): TemplateState => {
  const originalSorting = getAllUpdatedItems(state, serverTemplate.items)
  originalSorting.sort((a, b) => a.sortIndex - b.sortIndex)

  const fromIndex = originalSorting.findIndex(x => x.id === fromId)
  const toIndex = originalSorting.findIndex(x => x.id === toId)

  return arrayMove(originalSorting, fromIndex, toIndex)
    .map((x, i) => ({ item: x, newSortIndex: i }))
    .filter(x => x.newSortIndex !== x.item.sortIndex)
    .reduce((newState, x) => updateItemById(newState, serverTemplate, x.item.id, y => ({ ...y, sortIndex: x.newSortIndex })), state)
}

export const findTemplateItem = (state: TemplateState, serverTemplate: Template, templateItemId: number) => {
  return getAllUpdatedItems(state, serverTemplate.items).find(x => x.id === templateItemId)
}

export const viewItemForDraggableCatalogItem = (catalogItem: CatalogItem, dragId: string): ViewItem => {
  return {
    ...createTemplateItemFromCatalogItem(catalogItem),
    dragId: dragId,
    isEntireRowDisabled: false,
    isDeleted: false
  }
}

export const hasChanges = (state: TemplateState, templateId: number): boolean => {
  return templateId === 0 ||
    hasValue(state.name) ||
    hasValue(state.categories) ||
    hasValue(state.workareaInternalNotes) ||
    hasValue(state.workareaCustomerAndInvoiceNotes) ||
    hasValue(state.workareaInvoiceDescription) ||
    state.addedItems.length > 0 ||
    state.updatedItems.length > 0 ||
    state.deletedItemIds.length > 0
}

export const copyTemplate = (template: Template, newName: string): TemplateState => {
  return {
    name: newName,
    categories: template.categories,
    workareaCustomerAndInvoiceNotes: template.workareaCustomerAndInvoiceNotes,
    workareaInvoiceDescription: template.workareaInvoiceDescription,
    workareaInternalNotes: template.workareaInternalNotes,
    addedItems: template.items.map(x => ({ ...x, id: 0 })),
    updatedItems: [],
    deletedItemIds: []
  }
}

export const buildStateForSave = (state: TemplateState): TemplateState => {
  return {
    ...state,
    addedItems: state.addedItems.filter(x => !state.deletedItemIds.includes(x.id)),
    updatedItems: state.updatedItems.filter(x => !state.deletedItemIds.includes(x.id)),
  }
}

export const filterTemplates = (templates: TemplateSummary[], search: string): TemplateSummary[] => {
  const searchTerm = search.toLowerCase()
  return templates.filter(x => `${x.name.toLowerCase()}${x.categories?.toLowerCase() ?? ''}`.includes(searchTerm))
}